// Single source of truth for the active IdP and which login UIs are reachable.
//
// Mirrors the gateway/backend model (kural_auth `IDP_PROVIDER`): exactly ONE
// IdP per deployment, chosen by the single REACT_APP_IDP_TYPE env var — never
// two at once. There is no separate "enabled" flag per provider anymore.
//   idm     → legacy IDM cookie auth; the in-app /login picker is bypassed and
//             route guards / 401 handlers redirect to REACT_APP_REDIRECT_URL.
//   cognito → AWS Amplify Hosted UI (Bearer tokens); shows the Cognito picker.
//   entra   → Azure Entra / MSAL (Bearer tokens, incl. Teams SSO).
// See public/config.template.js for the canonical config notes.

import { environment } from "./environment";

export type IdpType = "idm" | "cognito" | "entra";

// Normalized, lowercased active IdP. Unknown / empty / misconfigured values
// FAIL CLOSED to "idm" — never silently activate Cognito or Entra on a tenant
// that didn't explicitly opt in (wrong pool / wrong IdP would violate the
// "one pool per tenant, never shared" rule).
const normalizeIdp = (raw: string | undefined | null): IdpType => {
	const v = (raw || "").trim().toLowerCase();
	if (v === "cognito") return "cognito";
	if (v === "entra") return "entra";
	return "idm";
};

export const idpType: IdpType = normalizeIdp(environment.REACT_APP_IDP_TYPE);

export const isCognitoLoginAllowed: boolean = idpType === "cognito";

// Entra / MSAL credential path (Bearer via MSAL or Teams SSO). Drives axios
// token acquisition, the 401 retry branch, and the TopNavbar logout flow.
export const isEntraEnabled: boolean = idpType === "entra";

// IDM is the implicit method for every non-Cognito tenant (idm + entra),
// preserving the historic pre-Cognito fallback.
export const isIdmLoginAllowed: boolean = !isCognitoLoginAllowed;

// True when IDM is the *only* reachable in-app method (i.e. no Cognito picker).
// In this case the /login picker is bypassed entirely and route guards / 401
// handlers redirect directly to REACT_APP_REDIRECT_URL.
export const isOnlyIdmAllowed: boolean = !isCognitoLoginAllowed;

// Anchor a domain-suffix match to a DNS label boundary: the domain must equal
// the suffix or end with ".<suffix>". Without this, an unanchored endsWith lets
// an attacker-registered "evilzs.com" match a "zs.com" allow-entry (VAPT 848793).
export const domainMatchesSuffix = (domain: string, suffix: string): boolean =>
	domain === suffix || domain.endsWith(`.${suffix}`);

// Parsed, lowercased ZS domain suffixes (e.g. ["zs.com", "zsservices.com"]).
// Sourced from REACT_APP_ZS_DOMAINS; used to gate ZS-only admin actions when
// Cognito login is enabled.
const zsDomainSuffixes: string[] = (environment.REACT_APP_ZS_DOMAINS || "")
	.split(",")
	.map((d) => d.trim().toLowerCase())
	.filter(Boolean);

/**
 * True when the email's domain is a ZS domain (matches REACT_APP_ZS_DOMAINS).
 * Returns false for empty/malformed emails. Domain comparison is suffix-based
 * and case-insensitive, mirroring the existing checks in AddUserModal.
 */
export const isZsEmail = (email: string | undefined | null): boolean => {
	const domain = email?.toLowerCase().split("@")[1];
	if (!domain) return false;
	return zsDomainSuffixes.some((suffix) => domainMatchesSuffix(domain, suffix));
};

// Parsed, lowercased allowed domain suffixes from REACT_APP_ALLOWED_DOMAINS.
// Only emails whose domain matches this list should be permitted to log in.
const allowedDomainSuffixes: string[] = (environment.REACT_APP_ALLOWED_DOMAINS || "")
	.split(",")
	.map((d) => d.trim().toLowerCase())
	.filter(Boolean);

/**
 * Returns the Cognito federated-IdP provider name for the given email.
 * ZS emails route to the ZS provider; everything else to the client provider.
 */
export const resolveProviderForEmail = (email: string): string => {
	return isZsEmail(email)
		? environment.REACT_APP_COGNITO_ZS_PROVIDER
		: environment.REACT_APP_COGNITO_CLIENT_PROVIDER;
};

/**
 * True when the email's domain is in the REACT_APP_ALLOWED_DOMAINS allowlist.
 * Returns false for empty/malformed emails or domains not in the list.
 */
export const isAllowedDomain = (email: string | undefined | null): boolean => {
	const domain = email?.toLowerCase().split("@")[1];
	if (!domain) return false;
	return allowedDomainSuffixes.some((suffix) => domainMatchesSuffix(domain, suffix));
};
